import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

interface DocumentationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DocumentationModal: React.FC<DocumentationModalProps> = ({ isOpen, onClose }) => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDocumentation();
    }
  }, [isOpen]);

  const loadDocumentation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Lade die Markdown-Datei direkt aus dem public Ordner
      const response = await fetch('/BENUTZERDOKUMENTATION.md');
      
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Dokumentation: ${response.status}`);
      }
      
      const content = await response.text();
      setMarkdownContent(content);
    } catch (err) {
      console.error('Fehler beim Laden der Dokumentation:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setIsLoading(false);
    }
  };

  // Verbesserter Markdown-zu-HTML Konverter
  const convertMarkdownToHTML = (markdown: string): string => {
    let html = markdown;
    
    // Headers (alle Ebenen) - Rot (#c00) als Hauptfarbe
    html = html
      .replace(/^###### (.*$)/gim, '<h6 style="color: #c00; margin-top: 15px; margin-bottom: 8px; font-size: 16px; font-weight: 600;">$1</h6>')
      .replace(/^##### (.*$)/gim, '<h5 style="color: #c00; margin-top: 18px; margin-bottom: 10px; font-size: 18px; font-weight: 600;">$1</h5>')
      .replace(/^#### (.*$)/gim, '<h4 style="color: #c00; margin-top: 0px; margin-bottom: 0px; font-size: 20px; font-weight: 600;">$1</h4>')
      .replace(/^### (.*$)/gim, '<h3 style="color: #c00; margin-top: 0px; margin-bottom: 0px; font-size: 22px; font-weight: 600;">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 style="color: #c00; margin-top: 30px; margin-bottom: 18px; font-size: 24px; font-weight: 600; border-bottom: 2px solid #e0e0e0; padding-bottom: 8px;">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 style="color: #c00; margin-top: 35px; margin-bottom: 20px; font-size: 28px; font-weight: 700;">$1</h1>');
    
    // Bold text
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 700; color: #333;">$1</strong>');
    
    // Italic text
    html = html.replace(/\*(.*?)\*/g, '<em style="font-style: italic; color: #555;">$1</em>');
    
    // Code blocks
    html = html.replace(/```([\s\S]*?)```/g, '<pre style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 16px; border-radius: 8px; overflow-x: auto; margin: 16px 0; font-family: \'Monaco\', \'Menlo\', \'Ubuntu Mono\', monospace; font-size: 14px; line-height: 1.4;"><code style="color: #333;">$1</code></pre>');
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code style="background: #f8f9fa; border: 1px solid #e9ecef; padding: 3px 6px; border-radius: 4px; font-family: \'Monaco\', \'Menlo\', \'Ubuntu Mono\', monospace; font-size: 13px; color: #e83e8c;">$1</code>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #c00; text-decoration: none; border-bottom: 1px solid #c00;" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Tabellen - Verbesserte Implementierung
    const tableRegex = /(\|.*\|[\s\S]*?)(?=\n[^|]|\n$)/g;
    html = html.replace(tableRegex, (tableMatch) => {
      const lines = tableMatch.trim().split('\n').filter(line => line.trim());
      if (lines.length < 2) return tableMatch;
      
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd; background: white;">';
      
      lines.forEach((line, index) => {
        // Überspringe Trennlinien (|---|---|)
        if (line.match(/^\|[\s\-|]+\|$/)) return;
        
        const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
        if (cells.length === 0) return;
        
        const isHeader = index === 0;
        const tag = isHeader ? 'th' : 'td';
        const cellStyle = isHeader 
          ? 'border: 1px solid #ddd; padding: 12px 8px; text-align: left; background: #f8f9fa; font-weight: 600; color: #c00;'
          : 'border: 1px solid #ddd; padding: 8px; text-align: left;';
        
        tableHtml += `<tr>${cells.map(cell => `<${tag} style="${cellStyle}">${cell}</${tag}>`).join('')}</tr>`;
      });
      
      tableHtml += '</table>';
      return tableHtml;
    });
    
    // Split into lines for better processing
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inList = false;
    let listItems: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for list items
      if (line.match(/^[\s]*[-*+]\s/) || line.match(/^[\s]*\d+\.\s/)) {
        if (!inList) {
          inList = true;
        }
        // Extract content after bullet/number
        const content = line.replace(/^[\s]*[-*+]\s/, '').replace(/^[\s]*\d+\.\s/, '');
        listItems.push(`<li style="margin: 4px 0; padding: 2px 0; line-height: 1.5;">${content}</li>`);
      } else {
        // Close list if we were in one
        if (inList && listItems.length > 0) {
          processedLines.push(`<ul style="margin: 12px 0; padding-left: 24px; list-style-type: disc;">${listItems.join('')}</ul>`);
          listItems = [];
          inList = false;
        }
        
        // Process regular lines
        if (line.trim() === '') {
          processedLines.push('<br>');
        } else if (!line.match(/^<h[1-6]/) && !line.match(/^<pre/) && !line.match(/^<ul/)) {
          // Only wrap in paragraph if it's not already a header, pre, or ul
          processedLines.push(`<p style="margin: 8px 0; line-height: 1.6; color: #333;">${line}</p>`);
        } else {
          processedLines.push(line);
        }
      }
    }
    
    // Close any remaining list
    if (inList && listItems.length > 0) {
      processedLines.push(`<ul style="margin: 12px 0; padding-left: 24px; list-style-type: disc;">${listItems.join('')}</ul>`);
    }
    
    return processedLines.join('\n');
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '1000px',
        height: '90%',
        maxHeight: '800px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#c00',
          borderRadius: '16px 16px 0 0'
        }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '24px', fontWeight: '600' }}>
            📚 Benutzerdokumentation
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Schliessen"
          >
            <CloseIcon style={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '24px',
          overflow: 'auto',
          backgroundColor: '#fafafa',
          lineHeight: '1.6'
        }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666' }}>📖 Dokumentation wird geladen...</div>
            </div>
          )}

          {error && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#d32f2f',
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>❌ Fehler beim Laden</div>
              <div>{error}</div>
              <button
                onClick={loadDocumentation}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {!isLoading && !error && markdownContent && (
            <div
              style={{
                lineHeight: '1.6',
                fontSize: '16px',
                color: '#333',
                maxWidth: '100%',
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}
              dangerouslySetInnerHTML={{
                __html: convertMarkdownToHTML(markdownContent)
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentationModal;
